import NotificationIcon from "../Icons/NotificationIcon";
import NotificationItemIcon from "../Icons/NotificationItemIcon";
import Dropdown from "./Dropdown";
import {
  GetAllNotificationsAdmin,
  GetAllNotificationsStudent,
} from "../../api/Notifications/GetAllNotifications";
import { useQuery, useMutation } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useUserDataStore } from "../../stores/user";
import { ReadNotifications } from "../../api/Notifications/ReadNotifications";

const NotificationsDropdown = () => {
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  const { data: NotificationsResponse, refetch } = useQuery({
    queryFn:
      userRole === "admin"
        ? GetAllNotificationsAdmin
        : GetAllNotificationsStudent,
    queryKey: ["notifications"],
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
    enabled: !!userData.id && userRole !== "sales"
  });

  const readNotificationsMutation = useMutation<void, Error, void>({
    mutationFn: ReadNotifications,
    onSuccess: () => {
      refetch(); // Refetch notifications after marking them as read
    },
    onError: (error) => {
      console.error("Error reading notifications:", error);
    },
  });

  const handleDropdownClose = () => {
    readNotificationsMutation.mutate(); // Trigger the mutation when dropdown closes
  };

  // Ensure that notifications is an array
  const notifications =
    NotificationsResponse && Array.isArray(NotificationsResponse.data)
      ? NotificationsResponse.data
      : [];
  const unreadNotifications =
    notifications?.filter((notification) => !notification.isRead) || [];

  // Set indicator to true if there are any unread notifications
  const hasUnreadNotifications = unreadNotifications.length > 0;

  return (
    <Dropdown
      indicator={hasUnreadNotifications} // Indicator is true if there are unread notifications
      title="Notifications"
      orientation="end"
      toggleImage={<NotificationIcon />}
      toggleClassNames="transition-colors duration-300"
      onClose={handleDropdownClose} // Handle dropdown close event
    >
      <div className="px-2 pt-2">
        {unreadNotifications?.length > 0 ? (
          unreadNotifications?.map((notification) => (
            <div
              key={notification.id}
              className="flex cursor-pointer gap-3.5 rounded-xl px-4 py-3 transition-colors duration-300 hover:bg-secondary/10"
              onClick={() => readNotificationsMutation.mutate()}
            >
              <div className="relative flex h-9 w-9 min-w-[36px] items-center justify-center rounded-xl border border-secondary/15 bg-secondary/10 text-secondary">
                <NotificationItemIcon />
              </div>
              <div>
                <p className="text-sm leading-snug text-lightGray">
                  <a className="cursor-pointer font-semibold text-primary hover:underline">
                    {notification.title}
                  </a>{" "}
                  {notification.message}
                </p>
                <small className="text-xs text-lightGray/70">
                  {formatDistanceToNow(new Date(notification.createdAt), {
                    addSuffix: true,
                  })}
                </small>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 px-5 py-7">
            <NotificationItemIcon aspects="40" />
            <p className="text-center text-sm text-lightGray">
              There are no new notifications.
            </p>
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default NotificationsDropdown;
